import React, { useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import { getTokenForRequest } from '../../utils/apiAuth';
import './GamificationReport.css';

/**
 * Helper function to encode token for API calls
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * Helper function to check if no student score in class
 */
const noStudentScoreInClass = (classesRanks, selectedClassId) => {
  if (selectedClassId < 0 && classesRanks && classesRanks.length > 0) {
    return false;
  } else if (classesRanks) {
    return classesRanks.filter(e => parseInt(e.ClassId) === parseInt(selectedClassId)).length <= 0;
  } else {
    return true;
  }
};

/**
 * GamificationReport Component
 * Migrated from angular/template/gamification-report-template.html
 */
const GamificationReport = () => {
  // State management
  const [semesters, setSemesters] = useState([]);
  const [moduleSemesters, setModuleSemesters] = useState([]);
  const [educatorClasses, setEducatorClasses] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState('');
  const [selectedModuleSemesterId, setSelectedModuleSemesterId] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState('-1');
  const [educatorClassRanks, setEducatorClassRanks] = useState([]);
  const [allClassRanks, setAllClassRanks] = useState([]);
  const [scoreOrder, setScoreOrder] = useState(false); // false = descending, true = ascending
  const [scoreOrderClass, setScoreOrderClass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      const token = getTokenForRequest();

      try {
        setLoading(true);
        
        // Load module semesters
        if (window.ReportUtilities) {
          const reportHelper = new window.ReportUtilities();
          reportHelper.GetAllModuleSemesters(token, (moduleSemestersData) => {
            if (moduleSemestersData) {
              setSemesters(moduleSemestersData.AllSemesters || []);
              setModuleSemesters(moduleSemestersData.ModuleSemesters || []);
              
              // Set default semester (first effective one)
              const effectiveSemester = moduleSemestersData.AllSemesters?.find(s => s.Effective);
              if (effectiveSemester) {
                setSelectedSemesterId(effectiveSemester.ID.toString());
              }
            }
          });
        }

        // Load educator classes
        if (window.OrganizationUtilities) {
          const orgHelper = new window.OrganizationUtilities();
          orgHelper.getEducatorClasses(token, (result) => {
            if (result && result.length > 0) {
              setEducatorClasses(result);
            }
            setLoading(false);
          });
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load initial data');
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Get gamification report
  const getGamificationReport = useCallback(async () => {
    if (!selectedModuleSemesterId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('./webservices/Gamification.svc/GetClassTopStudents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodeToken(getTokenForRequest()),
          limit: 30,
          msIds: [parseInt(selectedModuleSemesterId)],
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result) {
        setEducatorClassRanks(result);

        // When select all classes (selectedClassId === '-1')
        if (selectedClassId === '-1') {
          const mergedRanks = [];
          
          result.forEach((classRank) => {
            // If educator has access to the class
            if (educatorClasses.some(e => e.ID === classRank.ClassId)) {
              classRank.StudentRanks?.forEach((classStudentRank) => {
                classStudentRank.ClassName = classRank.ClassName;
                
                // Check if student already exists (by StudentId)
                if (!mergedRanks.some(e => e.StudentId === classStudentRank.StudentId)) {
                  mergedRanks.push(classStudentRank);
                }
              });
            }
          });

          setAllClassRanks(mergedRanks);
        }
        // When Selected all SP classes (selectedClassId === '-2')
        else if (selectedClassId === '-2') {
          const mergedRanks = [];
          
          result.forEach((classRank) => {
            // If educator has access to the class and not BB External
            if (educatorClasses.some(e => e.ID === classRank.ClassId) && 
                !classRank.ClassName?.includes('BB External ')) {
              classRank.StudentRanks?.forEach((classStudentRank) => {
                classStudentRank.ClassName = classRank.ClassName;
                
                // Check if student already exists (by StudentId)
                if (!mergedRanks.some(e => e.StudentId === classStudentRank.StudentId)) {
                  mergedRanks.push(classStudentRank);
                }
              });
            }
          });

          setAllClassRanks(mergedRanks);
        }
      }
    } catch (err) {
      console.error('Error loading gamification report:', err);
      if (window.CheckForAuthError && window.CheckForAuthError(err)) {
        return;
      }
      setError(err.message || 'Failed to load gamification report');
    } finally {
      setLoading(false);
    }
  }, [selectedModuleSemesterId, selectedClassId, educatorClasses]);

  // Auto-fetch report when module or class changes
  useEffect(() => {
    if (selectedModuleSemesterId && selectedClassId) {
      getGamificationReport();
    }
  }, [selectedModuleSemesterId, selectedClassId, getGamificationReport]);

  // Export CSV function
  const exportGamificationReport = useCallback(() => {
    // Only export when "All Classes" is selected (selectedClassId < 0, i.e., -1 or -2)
    // This matches the old behavior where only allClassRanks was exported
    if (parseInt(selectedClassId) >= 0) {
      alert('CSV export is only available when "All Classes" is selected.');
      return;
    }

    if (!allClassRanks || allClassRanks.length === 0) {
      alert('No data available.');
      return;
    }

    // Sort the data the same way it's displayed
    const sortedData = [...allClassRanks].sort((a, b) => {
      return scoreOrder ? a.Score - b.Score : b.Score - a.Score;
    });

    const csvFields = ['Rank', 'Student Name', 'Student Class', 'Student Score'];
    const csvData = sortedData.map((rank, index) => [
      index + 1,
      rank.StudentName,
      rank.ClassName,
      rank.Score
    ]);

    if (window.Papa && window.Papa.unparse) {
      const csvString = window.Papa.unparse({
        fields: csvFields,
        data: csvData
      });

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
      saveAs(blob, 'Gamification Report.csv');
    } else {
      console.error('PapaParse not available');
      alert('CSV export library not available. Please refresh the page.');
    }
  }, [allClassRanks, selectedClassId, scoreOrder]);

  // Filter module semesters by selected semester
  const filteredModuleSemesters = moduleSemesters.filter(
    ms => ms.SemesterID === parseInt(selectedSemesterId)
  );

  // Filter classes by selected module semester
  const filteredClasses = educatorClasses.filter(
    cls => cls.ModuleSemesterID === parseInt(selectedModuleSemesterId)
  );

  // Get selected class data
  const selectedClassData = educatorClassRanks.find(
    cr => parseInt(cr.ClassId) === parseInt(selectedClassId)
  );

  // Sort allClassRanks by score
  const sortedAllClassRanks = [...allClassRanks].sort((a, b) => {
    return scoreOrder ? a.Score - b.Score : b.Score - a.Score;
  });

  // Sort class student ranks by score
  const sortedClassRanks = selectedClassData?.StudentRanks 
    ? [...selectedClassData.StudentRanks].sort((a, b) => {
        return scoreOrderClass ? a.Score - b.Score : b.Score - a.Score;
      })
    : [];

  return (
    <div className="container-fluid gamification-report">
      {/* Filter Section */}
      <div className="row">
        <div className="col-sm-10">
          <div className="ic-filters ms-data-store">
            <label>Semester</label>
            <select 
              className="form-select ic-semesters" 
              value={selectedSemesterId}
              onChange={(e) => setSelectedSemesterId(e.target.value)}
            >
              {semesters.map((semester) => (
                <option key={semester.ID} value={semester.ID}>
                  {semester.Name}
                </option>
              ))}
            </select>

            <label>Module</label>
            <select 
              className="form-select ic-modules" 
              value={selectedModuleSemesterId || ''}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value) : null;
                setSelectedModuleSemesterId(value);
                if (selectedClassId === '-1') {
                  // Will trigger useEffect to fetch report
                }
              }}
            >
              <option value="">Select Module</option>
              {filteredModuleSemesters.map((moduleSemester) => (
                <option key={moduleSemester.ID} value={moduleSemester.ID}>
                  {moduleSemester.Module}
                </option>
              ))}
            </select>
          </div>

          <div className="ic-filters ms-data-store">
            <label>Class</label>
            <select 
              className="form-select ic-modules" 
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
            >
              <option value="-1">All</option>
              {filteredClasses.map((eduClass) => (
                <option key={eduClass.ID} value={eduClass.ID}>
                  {eduClass.Name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-sm-2">
          <div 
            className="cp-download" 
            onClick={exportGamificationReport}
            style={{ cursor: 'pointer', textAlign: 'center', padding: '10px' }}
            title="Export CSV"
          >
            <i className="fas fa-download" style={{ fontSize: '24px' }}></i>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center mt-3">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* All Classes View */}
      {selectedClassId < 0 && sortedAllClassRanks.length === 0 && !loading && (
        <div className="row mt-3">
          <div className="col-sm-12">
            <h4>No Data Available</h4>
          </div>
        </div>
      )}

      {selectedClassId < 0 && sortedAllClassRanks.length > 0 && (
        <div className="row mt-3">
          <div className="col-sm-12">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Class</th>
                    <th 
                      onClick={() => setScoreOrder(!scoreOrder)}
                      style={{ cursor: 'pointer' }}
                      className={scoreOrder ? 'dropup' : ''}
                    >
                      Score <span className="caret"></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAllClassRanks.map((rank, key) => (
                    <tr key={key}>
                      <th>{key + 1}</th>
                      <td>{rank.StudentName}</td>
                      <td>{rank.ClassName}</td>
                      <td>{rank.Score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Class Specific View */}
      {selectedClassId >= 0 && selectedClassData && (
        <div className="row mt-3">
          {sortedClassRanks.length === 0 && (
            <div className="col-sm-12">
              <h4>No Data Available</h4>
            </div>
          )}

          {sortedClassRanks.length > 0 && (
            <div className="col-sm-12">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th colSpan="3">{selectedClassData.Name}</th>
                    </tr>
                    <tr>
                      <td colSpan="3">
                        Students can {selectedClassData.ShowScore > 0 ? '' : 'not'} see score,
                        can {selectedClassData.LeaderboardType === 0 ? 'not' : ''} see leaderboard
                        {selectedClassData.LeaderboardType === 0 && (
                          <div style={{ color: 'red' }}>
                            <b>As educator, you can still see the ranking of students of the class.</b>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Rank</th>
                      <th>Name</th>
                      <th 
                        onClick={() => setScoreOrderClass(!scoreOrderClass)}
                        style={{ cursor: 'pointer' }}
                        className={scoreOrderClass ? 'dropup' : ''}
                      >
                        Score <span className="caret"></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedClassRanks.map((rank, key) => (
                      <tr key={key}>
                        <th>{key + 1}</th>
                        <td>{rank.StudentName}</td>
                        <td>{rank.Score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No student score in class message */}
      {selectedClassId >= 0 && noStudentScoreInClass(educatorClassRanks, selectedClassId) && (
        <div className="row mt-3">
          <div className="col-sm-12">
            <h4>No student in class received any score</h4>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamificationReport;

